import { useEffect, useRef } from "react";

interface UseLoadMoreOnScrollProps {
  hasNextPage: boolean;
  isFetching: boolean;
  isError?: boolean;
  onLoadMore: () => void;
}

// Returns a ref for a sentinel element placed after the list; `onLoadMore` runs
// when it scrolls into view, unless a page is already loading or the last one failed
export default function useLoadMoreOnScroll({
  hasNextPage,
  isFetching,
  isError = false,
  onLoadMore,
}: UseLoadMoreOnScrollProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const canLoadMore = hasNextPage && !isFetching && !isError;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !canLoadMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canLoadMore, onLoadMore]);

  return sentinelRef;
}
